import React from "react";
import {
  Datagrid,
  TextField,
  Filter,
  TextInput,
  CreateButton,
  FunctionField,
  Responsive,
  SimpleList,
  Record,
} from "react-admin";
import "../../common/styles/list-style.css";
import { ICompanyGridView } from "../../../common/data/data-types/companies";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { Box, useTheme, Theme, Tooltip } from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../../common/types";
import { useListContext } from "react-admin";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { IResourceListProps } from "../../common/ra-types";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { useFieldPermissions } from "../../../common/hooks/useFieldPermissions";
import { protectedEntities } from "../../../common/auth/permissions";
import { FieldPermissionsContext } from "../../common/field-permissions/FieldPermissionsContext";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import BusinessIcon from "@material-ui/icons/Business";
import LocationIcon from "@material-ui/icons/LocationOn";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import ScheduleIcon from "@material-ui/icons/Schedule";

const ListFilter = (props: any) => {
  useSearchFields(props, ["name"]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="companies-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="companies-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
    </Filter>
  );
};

export const CompaniesList = (props: IResourceListProps) => {
  const themeName = useSelector((state: AppState) => state.theme);
  const theme: Theme = useTheme();
  const permissions = useActionPermissions();
  const fieldPermissions = useFieldPermissions(
    protectedEntities.Company,
    (x) => x.list
  );

  const ListActions = () => {
    const { basePath } = useListContext();
    const permissions = useActionPermissions();
    return (
      <>
        {permissions &&
          permissions[permissionNames.admin.companies.main] &&
          permissions[permissionNames.admin.companies.create] && (
            <CreateButton basePath={basePath} />
          )}
      </>
    );
  };

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Tooltip title="Name">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="name">
              <TextField
                source="name"
                label="resources.admin-companies.labels.name"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <FunctionField<ICompanyGridView>
          source="status"
          headerClassName="centered-content"
          label="resources.common.labels.active"
          render={(record?: ICompanyGridView) => (
            <Box className="centered-content">
              {record?.status === "Active" ? (
                <CheckBoxIcon
                  className={record.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.success.dark
                        : theme.palette.success.light,
                  }}
                />
              ) : (
                <IndeterminateCheckBoxIcon
                  className={record?.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.error.dark
                        : theme.palette.error.light,
                  }}
                />
              )}
            </Box>
          )}
        />
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        className="hej"
        style={{
          marginTop: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <Tooltip title="Street">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <LocationIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="street">
              <TextField
                style={{ color: theme.palette.text.secondary }}
                source="street"
                label="resources.admin-companies.labels.street"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Dot">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <FiberManualRecordIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="dot">
              <TextField
                source="dot"
                style={{ color: theme.palette.text.secondary }}
                label="resources.admin-companies.labels.dot"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Dot">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <ScheduleIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="homeTerminalTimeZone">
              <TextField
                style={{ color: theme.palette.text.secondary }}
                source="homeTerminalTimeZone"
                label="resources.admin-companies.labels.homeTerminalTimeZone"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="content-container">
      <FieldPermissionsContext.Provider value={fieldPermissions}>
        <DefaultProLogsList
          empty={<ProLogsEmpty iconType={BusinessOutlinedIcon} />}
          className="content-list"
          title="resources.admin-companies.name"
          bulkActionButtons={false}
          actions={<ListActions />}
          filters={<ListFilter />}
          {...props}
        >
          <Responsive
            small={
              <SimpleList
                className="simple-list-wrapper"
                linkType="show"
                primaryText={(record) => (
                  <RenderPrimaryTextForSimpleList record={record} />
                )}
                secondaryText={(record) => (
                  <RenderSecondaryTextForSimpleList record={record} />
                )}
                rightIcon={(record) => {
                  return (
                    <ActionsField
                      hasShow={props.hasShow}
                      {...props}
                      record={record}
                      editable={() =>
                        permissions &&
                        permissions[permissionNames.admin.companies.main] &&
                        permissions[permissionNames.admin.companies.update]
                      }
                      canActivateOrDeactivate={() =>
                        permissions &&
                        permissions[permissionNames.admin.companies.main] &&
                        permissions[permissionNames.admin.companies.update]
                      }
                    ></ActionsField>
                  );
                }}
              />
            }
            medium={
              <Datagrid>
                <ProtectedElement source="name">
                  <TextField
                    source="name"
                    label="resources.admin-companies.labels.name"
                  />
                </ProtectedElement>
                <ProtectedElement source="street">
                  <TextField
                    source="street"
                    label="resources.admin-companies.labels.street"
                  />
                </ProtectedElement>
                <ProtectedElement source="dot">
                  <TextField
                    source="dot"
                    label="resources.admin-companies.labels.dot"
                  />
                </ProtectedElement>
                <ProtectedElement source="homeTerminalTimeZone">
                  <TextField
                    source="homeTerminalTimeZone"
                    label="resources.admin-companies.labels.homeTerminalTimeZone"
                  />
                </ProtectedElement>
                <FunctionField<ICompanyGridView>
                  source="status"
                  headerClassName="centered-content"
                  label="resources.common.labels.active"
                  render={(record?: ICompanyGridView) => (
                    <Box className="centered-content">
                      {record?.status === "Active" ? (
                        <CheckBoxIcon
                          className={record.status}
                          style={{
                            color:
                              themeName === "dark"
                                ? theme.palette.success.dark
                                : theme.palette.success.light,
                          }}
                        />
                      ) : (
                        <IndeterminateCheckBoxIcon
                          className={record?.status}
                          style={{
                            color:
                              themeName === "dark"
                                ? theme.palette.error.dark
                                : theme.palette.error.light,
                          }}
                        />
                      )}
                    </Box>
                  )}
                />
                <ActionsField
                  hasShow={props.hasShow}
                  editable={() =>
                    permissions &&
                    permissions[permissionNames.admin.companies.main] &&
                    permissions[permissionNames.admin.companies.update]
                  }
                  canActivateOrDeactivate={() =>
                    permissions &&
                    permissions[permissionNames.admin.companies.main] &&
                    permissions[permissionNames.admin.companies.update]
                  }
                ></ActionsField>
              </Datagrid>
            }
          />
        </DefaultProLogsList>
      </FieldPermissionsContext.Provider>
    </div>
  );
};
